/**
 * Hook for fetching complete space hierarchy of a project
 * including all plots and their locations.
 */

import { gql, useQuery } from "@apollo/client";
import type { Location, Plot } from "@/types";

export interface PlotWithLocations extends Plot {
  locations: Location[];
}

export interface ProjectSpaceData {
  id: string;
  name: string;
  plots: PlotWithLocations[];
  locations: Location[];
}

const GET_PROJECT_SPACE = gql`
  query GetProjectSpace($id: String!) {
    project(id: $id) {
      id
      name
      plots {
        id
        projectId
        name
        locations {
          id
          name
          description
          locationType
          area
          projectId
          plotId
          parentLocationId
          schedulingPlatformSpaceId
          schedulingPlatformSpaceParentId
          version
          createdAt
          updatedAt
        }
      }
      locations {
        id
        name
        description
        locationType
        area
        projectId
        plotId
        parentLocationId
        schedulingPlatformSpaceId
        schedulingPlatformSpaceParentId
        version
        createdAt
        updatedAt
      }
    }
  }
`;

interface ProjectSpaceQueryResult {
  project: ProjectSpaceData | null;
}

export function useProjectSpace(projectId: string | null) {
  const { data, loading, error } = useQuery<ProjectSpaceQueryResult>(
    GET_PROJECT_SPACE,
    {
      variables: { id: projectId },
      skip: !projectId,
      fetchPolicy: "cache-and-network",
    },
  );

  return {
    projectSpace: data?.project || null,
    loading,
    error,
  };
}
